import { Divider } from 'src/design/atoms/Divider';
import { Grid } from 'src/design/atoms/Grid';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { IconFileTypePdf, IconFileTypeXls } from '@tabler/icons-react';
import fileDownload from 'js-file-download';
import { isEqual } from 'lodash';
import React from 'react';
import { OrganizationOfficeDto } from 'src/api/FiretrolApiClient';
import {
  formatWorkWeekId,
  getCurrentWorkWeek,
  parseWorkWeekId,
} from 'src/utils/workWeek';
import {
  IPayrollPdfRequest,
  useTimesheetExcelExport,
  useTimesheetPayrollPdf,
} from 'src/api/queries/timesheets/useTimesheetReports';
import { OfficeSelector } from 'src/app/selectors/OfficeSelectorSingle';
import { useAuth } from 'src/hooks/useAuth';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Header } from 'src/design/organisms/Header';
import { PdfViewer } from 'src/design/organisms/PdfViewer/PdfViewer';
import { usePdfFile } from 'src/design/molecules/usePdfFile';
import { JobUserSelect } from 'src/app/selectors/JobUserSelectSingle';
import {
  currentWeekFilter,
  tempOptions,
  WeekSelector,
} from 'src/routes/Jobs/Timesheets/components/WeekSelector';
import { IDateOption } from 'src/routes/Jobs/Timesheets/types';

export const TimesheetPayrollReport: React.FC = () => {
  const { user } = useAuth();
  const [office, setOffice] = React.useState<OrganizationOfficeDto | null>(
    null,
  );
  const [selectedWeek, setSelectedWeek] = React.useState<IDateOption>(
    tempOptions.find(
      (o) =>
        o.id ===
        formatWorkWeekId(
          getCurrentWorkWeek().year,
          getCurrentWorkWeek().weekNumber,
        ),
    ) || currentWeekFilter,
  );
  const [selectedUserId, setSelectedUserId] = React.useState<number | null>(
    null,
  );
  const [showOnlyMyTeam, setShowOnlyMyTeam] = React.useState(false);
  // Each server render is expensive, so the PDF follows the filters only when
  // Generate Report is pressed, not on every filter change.
  const [submitted, setSubmitted] = React.useState<IPayrollPdfRequest | null>(
    null,
  );

  const { year: selectedReportYear, weekNumber: selectedWeekNumber } =
    parseWorkWeekId(selectedWeek.id);

  const request: IPayrollPdfRequest | null = office
    ? {
        officeId: office.id,
        week: selectedWeekNumber,
        year: selectedReportYear,
        userId: selectedUserId ?? undefined,
        onlyMyTeam: showOnlyMyTeam || undefined,
      }
    : null;

  const report = useTimesheetPayrollPdf(submitted);
  const pdf = usePdfFile(report.data);
  const loading = report.isFetching;

  const excelExport = useTimesheetExcelExport();
  const excelLoading = excelExport.isPending;

  const generateReport = () => {
    if (!request) return;
    if (isEqual(request, submitted)) void report.refetch();
    else setSubmitted(request);
  };

  const handleDownloadExcel = async () => {
    if (!office) return;
    const data = await excelExport
      .mutateAsync({
        officeId: office.id,
        weekId: selectedWeekNumber,
        year: selectedReportYear,
        weekEndDisplayDate: selectedWeek.endDate,
        userId: selectedUserId ?? undefined,
        onlyMyTeam: showOnlyMyTeam || undefined,
      })
      .catch(() => null);
    if (data) {
      fileDownload(data.data, `${office.name}_${selectedWeek.label}.xlsx`);
    }
  };

  let emptyText = 'No report to display.';
  if (!submitted) {
    emptyText = 'Choose an office and week, then select Generate Report.';
  } else if (report.isError) {
    emptyText = 'The report could not be generated.';
  }

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [
            { text: 'Reports', href: '/reports' },
            { text: 'Timesheet Payroll Report' },
          ],
        }}
        title="Timesheet Payroll Report"
        helpText="Generate timesheet statements for your organization."
      />
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 3, lg: 2 }}>
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Title order={5}>Generate</Title>
              <Input.Wrapper label="Office" withAsterisk>
                <OfficeSelector
                  onChange={() => {}}
                  onOfficeChange={(o) => setOffice(o)}
                  initialOfficeId={office?.id}
                  initialValue={''}
                />
              </Input.Wrapper>
              <Input.Wrapper label="Week" withAsterisk>
                <WeekSelector
                  hideLabel
                  value={selectedWeek}
                  onChange={(v) => setSelectedWeek(v)}
                />
              </Input.Wrapper>
              <Checkbox
                label="Show Only My Team"
                description="Only show timesheets for users that are supervised by you"
                checked={showOnlyMyTeam}
                onChange={(e) => setShowOnlyMyTeam(e.currentTarget.checked)}
              />
              <Input.Wrapper label="Employee">
                <JobUserSelect
                  hideLabel
                  small
                  hideRole
                  selectedUserId={selectedUserId}
                  office={office?.name}
                  supervisorId={showOnlyMyTeam ? user?.id : undefined}
                  onChange={(value) => {
                    if (value) {
                      setSelectedUserId(value.userId);
                    } else {
                      setSelectedUserId(null);
                    }
                  }}
                />
              </Input.Wrapper>
              <BasicButton
                disabled={!office || loading}
                onClick={generateReport}
                loading={loading}
                fullWidth
              >
                Generate Report
              </BasicButton>
              <Divider />
              <Title order={5}>Download</Title>
              <Stack gap="sm">
                <BasicButton
                  onClick={pdf.download}
                  disabled={!pdf.url || loading}
                  fullWidth
                  variant="light"
                  leftSection={<IconFileTypePdf size={18} />}
                >
                  Download PDF
                </BasicButton>
                <BasicButton
                  onClick={handleDownloadExcel}
                  disabled={!office || excelLoading}
                  loading={excelLoading}
                  fullWidth
                  variant="light"
                  leftSection={<IconFileTypeXls size={18} />}
                >
                  Payroll Report
                </BasicButton>
              </Stack>
            </Stack>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9, lg: 10 }}>
          <PdfViewer
            src={pdf.url}
            title="Timesheet Payroll Report PDF"
            loading={loading}
            emptyText={emptyText}
          />
        </Grid.Col>
      </Grid>
    </>
  );
};
